import { COLORS } from '../../config';
import { TaskInfo, TouchPoint, Trip } from '../../models/types';
import { getTripWindowMetrics } from '../../utils/formatters';

export interface TimelineMarker {
  id: string;
  kind: 'depot-start' | 'stop' | 'depot-end';
  label: string;
  shortLabel: string;
  color: string;
  minute: number;
  serviceStart: number;
  serviceEnd: number;
  windowStart: number | null;
  windowEnd: number | null;
  durationMinutes: number | null;
  hasTimeBreach: boolean;
  touchPoint: TouchPoint | null;
}

export interface TimelineStripData {
  tripName: string;
  vehicleLabel: string;
  tripColor: string;
  tripStart: number;
  tripEnd: number;
  shiftStart: number | null;
  shiftEnd: number | null;
  markers: TimelineMarker[];
}

export interface TimelineBounds {
  start: number;
  end: number;
  duration: number;
  contentWidth: number;
  pixelsPerMinute: number;
}

const DEFAULT_STRIP_MINUTES = 180;
const DEFAULT_BASE_WIDTH = 780;
const DEFAULT_MIN_WIDTH = 320;
const TIMELINE_EDGE_PADDING_MINUTES = 24;

const normalizeHash = (value: string) =>
  Math.abs(value.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0));

export const resolveTripColor = (trip: Trip, tripIndex?: number): string => {
  const isUnallocated = String(trip.tripMeta?.status || trip.slotTags?.[0] || '').toUpperCase() === 'UNALLOCATED';
  if (isUnallocated) {
    return '#9e9e9e';
  }
  const colorIndex = typeof tripIndex === 'number' && tripIndex >= 0
    ? tripIndex
    : normalizeHash(trip.name);
  return COLORS.tripColors[colorIndex % COLORS.tripColors.length];
};

const resolveVerifyPincodeTouchPointColor = (touchPoint: TouchPoint | null | undefined): string => {
  switch (touchPoint?.geoStatus) {
    case 'corrected':
      return '#7c3aed';
    case 'low_confidence':
      return '#DE3163';
    case 'derived':
      return '#e5ac62';
    default:
      return '#16a34a';
  }
};

const resolveTouchPointMarkerColor = (touchPoint: TouchPoint): string => {
  const tags = touchPoint.slotTags || [];
  const isOrigin = tags.includes('ORIGIN') || touchPoint.name.toLowerCase().includes('origin');
  const isPickup = tags.includes('PICKUP');
  const isDrop = tags.includes('DROP');
  const isUnassigned = tags.includes('UNASSIGNED');

  if (touchPoint.geoStatus && !isOrigin && !isPickup) {
    return resolveVerifyPincodeTouchPointColor(touchPoint);
  }
  if (isOrigin) {
    return '#1e88e5';
  }
  if (isPickup) {
    return '#2e7d32';
  }
  if (isDrop) {
    return '#ef6c00';
  }
  if (isUnassigned) {
    return '#6a1b9a';
  }

  return COLORS.touchPointAccuracy[touchPoint.accuracyLevel ?? 'HIGH'];
};

const parseClockMinutes = (raw?: string): number | null => {
  const value = String(raw ?? '').trim().toUpperCase();
  if (!value) return null;
  if (value === '24:00') return 1440;
  const match = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?$/);
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[4];
  if (meridiem === 'AM' && hours === 12) {
    hours = 0;
  } else if (meridiem === 'PM' && hours !== 12) {
    hours += 12;
  }
  return (hours * 60) + minutes;
};

const parseExtraClockMinutes = (value: unknown): number | null => {
  if (value == null) return null;
  return parseClockMinutes(String(value));
};

const getFiniteNumber = (value: unknown): number | null => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const normalizeRange = (start: number, end: number): { start: number; end: number } => {
  if (end >= start) {
    return { start, end };
  }
  return { start, end: end + 1440 };
};

const alignRangeToReference = (
  start: number,
  end: number,
  reference: number
): { start: number; end: number } => {
  let nextStart = start;
  let nextEnd = end;
  const center = (start + end) / 2;
  const offset = Math.round((reference - center) / 1440);
  nextStart += offset * 1440;
  nextEnd += offset * 1440;

  while (nextStart - reference > 720) {
    nextStart -= 1440;
    nextEnd -= 1440;
  }

  while (reference - nextEnd > 720) {
    nextStart += 1440;
    nextEnd += 1440;
  }

  return { start: nextStart, end: nextEnd };
};

const getShiftRange = (trip: Trip, referenceStart: number): { start: number; end: number } | null => {
  const value = trip.vehDetails.workingTime;
  if (!value) return null;
  const parts = value.split('-');
  if (parts.length < 2) return null;
  const start = parseClockMinutes(parts[0]);
  const end = parseClockMinutes(parts[1]);
  if (start == null || end == null) return null;
  const normalized = normalizeRange(start, end);
  return alignRangeToReference(normalized.start, normalized.end, referenceStart);
};

const getTouchPointRange = (
  touchPoint: TouchPoint,
  referenceStart: number
): { start: number; end: number; windowStart: number | null; windowEnd: number | null } => {
  const attemptStart = Number.isFinite(touchPoint.attemptTime?.start) ? Number(touchPoint.attemptTime.start) : null;
  const attemptEnd = Number.isFinite(touchPoint.attemptTime?.end) ? Number(touchPoint.attemptTime.end) : null;
  const firstWindow = touchPoint.openHoursMinOfDay?.[0];
  const rawWindowStart = Number.isFinite(firstWindow?.start) ? Number(firstWindow?.start) : null;
  const rawWindowEnd = Number.isFinite(firstWindow?.end) ? Number(firstWindow?.end) : null;

  const baseStart = attemptStart ?? rawWindowStart ?? referenceStart;
  const durationHint = Number(touchPoint.deliveryDurationMinutes);
  const hintedEnd = Number.isFinite(durationHint) && durationHint > 0
    ? baseStart + Math.round(durationHint)
    : baseStart;
  const baseEnd = attemptEnd ?? rawWindowEnd ?? hintedEnd;

  const normalized = normalizeRange(baseStart, baseEnd);
  const alignedService = alignRangeToReference(normalized.start, normalized.end, referenceStart);

  const alignedWindow = rawWindowStart != null && rawWindowEnd != null
    ? alignRangeToReference(
        ...(() => {
          const windowRange = normalizeRange(rawWindowStart, rawWindowEnd);
          return [windowRange.start, windowRange.end] as [number, number];
        })(),
        referenceStart
      )
    : null;

  return {
    start: alignedService.start,
    end: alignedService.end,
    windowStart: alignedWindow?.start ?? null,
    windowEnd: alignedWindow?.end ?? null,
  };
};

const buildStopMarkers = (trip: Trip, tripStart: number): TimelineMarker[] =>
  trip.tpDetails
    .slice()
    .sort((left, right) => left.seqNo - right.seqNo)
    .map((touchPoint) => {
      const range = getTouchPointRange(touchPoint, tripStart);
      const minute = range.start;
      const duration = Math.max(0, range.end - range.start);
      return {
        id: `${touchPoint.tpId}::${touchPoint.seqNo}`,
        kind: 'stop',
        label: touchPoint.name || touchPoint.tpId,
        shortLabel: `${touchPoint.seqNo}`,
        color: resolveTouchPointMarkerColor(touchPoint),
        minute,
        serviceStart: range.start,
        serviceEnd: range.end,
        windowStart: range.windowStart,
        windowEnd: range.windowEnd,
        durationMinutes: duration > 0 ? duration : null,
        hasTimeBreach:
          Number(touchPoint.timeBreachMin ?? 0) > 0 ||
          (
            range.windowStart != null &&
            range.windowEnd != null &&
            (range.start < range.windowStart || range.end > range.windowEnd)
          ),
        touchPoint,
      };
    });

export const buildTimelineStripData = (trip: Trip, tripIndex?: number): TimelineStripData | null => {
  const tripWindow = getTripWindowMetrics(trip);
  const tripExtra = trip.tripMeta?.extra ?? {};
  const explicitStart = parseExtraClockMinutes((tripExtra as Record<string, unknown>).tripStartTime);
  const explicitEnd = parseExtraClockMinutes((tripExtra as Record<string, unknown>).tripEndTime);
  const assignmentStart = getFiniteNumber(trip.assignmentMeta?.originStartMinutes);
  const assignmentDuration = getFiniteNumber(trip.assignmentMeta?.tripDurationMinutes);
  const fallbackAssignmentStart = tripWindow.startMinutes == null ? assignmentStart : null;
  const startCandidate = explicitStart ?? fallbackAssignmentStart;

  if (tripWindow.startMinutes == null && startCandidate == null) {
    return null;
  }

  const tripStart = startCandidate ?? tripWindow.startMinutes;
  if (tripStart == null || !Number.isFinite(tripStart)) {
    return null;
  }

  const explicitRange = explicitStart != null && explicitEnd != null
    ? normalizeRange(explicitStart, explicitEnd)
    : null;
  const rawTripEnd = explicitRange?.end
    ?? (startCandidate != null && assignmentDuration != null && assignmentDuration > 0
      ? tripStart + Math.round(assignmentDuration)
      : null)
    ?? tripWindow.endMinutes
    ?? (tripStart + Math.max(0, Math.round(tripWindow.durationMinutes ?? 0)));
  const tripEnd = rawTripEnd >= tripStart ? rawTripEnd : rawTripEnd + 1440;

  const shiftRange = getShiftRange(trip, tripStart);
  const tripColor = resolveTripColor(trip, tripIndex);
  const markers = [
    {
      id: `${trip.name}::depot-start`,
      kind: 'depot-start' as const,
      label: 'Depot',
      shortLabel: 'DEP',
      color: tripColor,
      minute: tripStart,
      serviceStart: tripStart,
      serviceEnd: tripStart,
      windowStart: null,
      windowEnd: null,
      durationMinutes: null,
      hasTimeBreach: false,
      touchPoint: null,
    },
    ...buildStopMarkers(trip, tripStart),
    {
      id: `${trip.name}::depot-end`,
      kind: 'depot-end' as const,
      label: 'Depot',
      shortLabel: 'DEP',
      color: tripColor,
      minute: tripEnd,
      serviceStart: tripEnd,
      serviceEnd: tripEnd,
      windowStart: null,
      windowEnd: null,
      durationMinutes: null,
      hasTimeBreach: false,
      touchPoint: null,
    },
  ];

  return {
    tripName: trip.name,
    vehicleLabel: trip.vehDetails.vehId || trip.name,
    tripColor,
    tripStart,
    tripEnd,
    shiftStart: shiftRange?.start ?? null,
    shiftEnd: shiftRange?.end ?? null,
    markers,
  };
};

export const canBuildTimelineStripData = (trip: Trip): boolean => {
  return buildTimelineStripData(trip) != null;
};

export const hasTimelineSupport = (taskInfo: TaskInfo | null | undefined): boolean => {
  return (taskInfo?.trips ?? []).some((trip) => canBuildTimelineStripData(trip));
};

export const buildTimelineStrips = (
  taskInfo: TaskInfo | null | undefined,
  tripNames: string[]
): TimelineStripData[] => {
  if (!taskInfo) {
    return [];
  }

  const tripsByName = new Map(taskInfo.trips.map((trip, index) => [trip.name, { trip, index }]));

  return tripNames
    .map((tripName) => tripsByName.get(tripName))
    .filter((entry): entry is { trip: Trip; index: number } => Boolean(entry))
    .map(({ trip, index }) => buildTimelineStripData(trip, index))
    .filter((strip): strip is TimelineStripData => Boolean(strip));
};

const getDensityScale = (strips: TimelineStripData[], duration: number): number => {
  const maxStops = Math.max(
    0,
    ...strips.map((strip) => strip.markers.filter((marker) => marker.kind === 'stop').length)
  );
  if (maxStops <= 1) {
    return 0;
  }
  return ((maxStops - 1) * 40) / Math.max(60, duration);
};

export const buildTimelineBounds = (strips: TimelineStripData[]): TimelineBounds => {
  if (!strips.length) {
    return {
      start: 0,
      end: DEFAULT_STRIP_MINUTES,
      duration: DEFAULT_STRIP_MINUTES,
      contentWidth: DEFAULT_MIN_WIDTH,
      pixelsPerMinute: DEFAULT_BASE_WIDTH / DEFAULT_STRIP_MINUTES,
    };
  }

  const allStarts = strips.flatMap((strip) => [
    strip.tripStart,
    strip.shiftStart ?? strip.tripStart,
  ]);
  const allEnds = strips.flatMap((strip) => [
    strip.tripEnd,
    strip.shiftEnd ?? strip.tripEnd,
  ]);

  const start = Math.min(...allStarts) - TIMELINE_EDGE_PADDING_MINUTES;
  const end = Math.max(...allEnds) + TIMELINE_EDGE_PADDING_MINUTES;
  const duration = Math.max(60, end - start);
  const densityScale = getDensityScale(strips, duration);
  const baseScale = DEFAULT_BASE_WIDTH / Math.max(DEFAULT_STRIP_MINUTES, duration);
  const pixelsPerMinute = Math.max(1.85, baseScale, densityScale);
  const contentWidth = Math.max(DEFAULT_MIN_WIDTH, Math.ceil(duration * pixelsPerMinute));

  return {
    start,
    end,
    duration,
    contentWidth,
    pixelsPerMinute,
  };
};

export const formatTimelineTick = (minutes: number): string => {
  const normalized = ((minutes % 1440) + 1440) % 1440;
  const hours24 = Math.floor(normalized / 60);
  const mins = normalized % 60;
  const suffix = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  if (mins === 0) {
    return `${hours12}${suffix}`;
  }
  return `${hours12}:${String(mins).padStart(2, '0')}${suffix}`;
};

export const getTimelineTicks = (start: number, end: number): number[] => {
  const duration = Math.max(30, end - start);
  const step = duration <= 300 ? 30 : 60;
  const firstTick = Math.ceil(start / step) * step;
  const ticks: number[] = [];
  for (let minute = firstTick; minute <= end; minute += step) {
    ticks.push(minute);
  }
  if (!ticks.length || ticks[ticks.length - 1] !== end) {
    ticks.push(end);
  }
  return ticks;
};

export const projectTimelineMinute = (
  minute: number,
  bounds: TimelineBounds
): number => {
  if (bounds.end <= bounds.start) return 0;
  return ((minute - bounds.start) / (bounds.end - bounds.start)) * bounds.contentWidth;
};
